import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, NEVER, Observable, of, throwError, timer } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { anomalyFixtures, recommendationFixtures } from '../domain/ai-insights.fixtures';
import { Page } from '../domain/page.model';
import {
  AiAnomaly,
  AiAnomalyListFilter,
  AiAnomalyStatusRequest,
  AiInsightKind,
  AiInsightsListView,
  AiInsightsPreview,
  AiRecommendation,
  AiRecommendationListFilter,
  AiDataFreshness,
  AiStatus,
  AiSummary,
} from '../domain/ai-insights.model';
import { HttpUtils } from '../utils/http-utils';
import moment from 'moment';

export interface AiInsightsChrome {
  subtitle: string;
  dataAsOf: string | null;
  showStamp: boolean;
  sample: boolean;
  /** Backend freshness for the stamp colour. Null falls back to the elapsed-time rule. */
  freshness?: AiDataFreshness | null;
}

const EMPTY_SUMMARY: AiSummary = {
  openHighSeverityAnomalies: 0,
  pendingRecommendations: 0,
};

/**
 * AI recommendations, anomalies, and the sidebar summary.
 * `environment.aiInsightsMock` is on only for a local serve. Dev, QA, and prod call /api/ai.
 */
@Injectable({
  providedIn: 'root',
})
export class AiInsightsService {
  readonly useMock = environment.aiInsightsMock;

  private readonly base = `${environment.serviceUrl}/api/ai`;
  private readonly previewSubject = new BehaviorSubject<AiInsightsPreview>('ready');
  private readonly reloadSubject = new BehaviorSubject<void>(undefined);
  private readonly chromeSubject = new BehaviorSubject<AiInsightsChrome>({
    subtitle: 'AI-assisted recommendations from recent performance',
    dataAsOf: null,
    showStamp: false,
    sample: false,
  });
  private readonly summaryRefresh$ = new BehaviorSubject<void>(undefined);

  readonly chrome$ = this.chromeSubject.asObservable();
  readonly preview$ = this.previewSubject.asObservable();

  /** One shared poll for every sidebar badge. */
  readonly summary$: Observable<AiSummary> = this.summaryRefresh$.pipe(
    switchMap(() => timer(0, 300000).pipe(
      // A failed poll shows no badge until the next tick. It must not end the stream.
      switchMap(() => this.useMock ? of(EMPTY_SUMMARY) : this.httpClient.get<AiSummary>(`${this.base}/summary`).pipe(
        catchError(() => of(EMPTY_SUMMARY))
      ))
    )),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly view$ = combineLatest([this.previewSubject, this.reloadSubject]).pipe(
    map(([preview]) => preview)
  );

  get preview(): AiInsightsPreview {
    return this.previewSubject.value;
  }

  constructor(private httpClient: HttpClient) {}

  setPreview(preview: AiInsightsPreview): void {
    this.previewSubject.next(preview);
  }

  setChrome(chrome: AiInsightsChrome): void {
    this.chromeSubject.next(chrome);
  }

  reload(): void {
    this.reloadSubject.next();
  }

  refreshSummary(): void {
    this.summaryRefresh$.next();
  }

  recommendations(filter: AiRecommendationListFilter): Observable<Page<AiRecommendation>> {
    return this.httpClient.get<Page<AiRecommendation>>(`${this.base}/recommendations`, {
      params: listParams(filter),
    });
  }

  approve(id: string): Observable<AiRecommendation> {
    return this.httpClient.put<AiRecommendation>(`${this.base}/recommendations/${id}/approve`, {});
  }

  reject(id: string, reason: string): Observable<AiRecommendation> {
    return this.httpClient.put<AiRecommendation>(`${this.base}/recommendations/${id}/reject`, { reason });
  }

  anomalies(filter: AiAnomalyListFilter): Observable<Page<AiAnomaly>> {
    return this.httpClient.get<Page<AiAnomaly>>(`${this.base}/anomalies`, {
      params: listParams(filter),
    });
  }

  setAnomalyStatus(id: string, body: AiAnomalyStatusRequest): Observable<AiAnomaly> {
    return this.httpClient.put<AiAnomaly>(`${this.base}/anomalies/${id}/status`, body);
  }

  summary(): Observable<AiSummary> {
    return this.summary$;
  }

  /** Engine switches and data freshness. Null when unreadable, so the list still loads. */
  status(): Observable<AiStatus | null> {
    return this.httpClient.get<AiStatus>(`${this.base}/status`).pipe(
      catchError(() => of(null))
    );
  }

  /** Local preview only. Live screens call recommendations() / anomalies(). */
  mockView(kind: AiInsightKind, preview: AiInsightsPreview): Observable<AiInsightsListView> {
    if (preview === 'loading') {
      return NEVER;
    }
    if (preview === 'error') {
      return throwError(() => new Error('AI insights are temporarily unavailable.'));
    }
    const dataAsOf = preview === 'stale' ? moment.utc().subtract(3, 'hours').toISOString() : null;
    const status = mockStatus(preview, dataAsOf);
    if (preview === 'empty' || preview === 'off' || kind === 'yield' || kind === 'forecasts') {
      return of({ cards: [], totalElements: 0, summary: EMPTY_SUMMARY, dataAsOf, status });
    }
    const cards = kind === 'anomalies' ? anomalyFixtures() : recommendationFixtures();
    return of({
      cards,
      totalElements: cards.length,
      summary: {
        openHighSeverityAnomalies: kind === 'anomalies' ? 1 : 0,
        pendingRecommendations: kind === 'recommendations' ? 2 : 0,
      },
      dataAsOf,
      status,
    });
  }
}

/** Preview stand-in for GET /api/ai/status. */
function mockStatus(preview: AiInsightsPreview, dataAsOf: string | null): AiStatus {
  const on = preview !== 'off';
  return {
    features: { BID: on, PACING: on, ANOMALY: on, YIELD: on, CREATIVE: on, AUDIENCE: on, FORECAST: on, EXTERNAL: on },
    dataFreshness: preview === 'stale' ? 'STALE' : 'FRESH',
    ...(dataAsOf ? { lastRebuiltAt: dataAsOf } : {}),
  };
}

function listParams(filter: AiRecommendationListFilter | AiAnomalyListFilter): HttpParams {
  const { sortProperty, ...rest } = filter;
  let params = HttpUtils.getHttpParams(rest);
  const sorts = sortProperty?.length ? sortProperty : ['createdOn'];
  sorts.forEach(property => {
    params = params.append('sortProperty', property);
  });
  if (!filter.sortDirection) {
    params = params.set('sortDirection', 'DESC');
  }
  return params;
}
